/**
 * Praevue Design System — Theme Tokens
 *
 * Brand rules (locked):
 * - Blue-600 #1D4ED8 → primary action, key highlight
 * - Blue-700 #1E40AF → active states, stronger emphasis
 * - Blue-300 #93C5FD → softer highlight, focus ring
 * - Danger-600 #DC2626 → negative meaning only (max loss, downside)
 * - No green as main positive color
 * - No gradients, neon, glow, confetti
 */

// ── Colors ──────────────────────────────────────────────────
export const colors = {
  // Brand blues
  blue600: "#1D4ED8",
  blue700: "#1E40AF",
  blue300: "#93C5FD",
  blue100: "#DBEAFE",
  blue50: "#EFF6FF",

  // Danger — negative meaning only
  danger600: "#DC2626",
  dangerLight: "#FEE2E2",

  // Warning — structural emphasis
  warning600: "#D97706",
  warningLight: "#FEF3C7",

  // Neutrals
  white: "#FFFFFF",
  gray50: "#F9FAFB",
  gray100: "#F3F4F6",
  gray200: "#E5E7EB",
  gray300: "#D1D5DB",
  gray400: "#9CA3AF",
  gray500: "#6B7280",
  gray600: "#4B5563",
  gray700: "#374151",
  gray800: "#1F2937",
  gray900: "#111827",
};

// ── Semantic aliases ────────────────────────────────────────
export const theme = {
  bg: colors.white,
  primary: colors.blue600,
  primaryLight: "#3B82F6",
  primaryDark: colors.blue700,
  primarySoft: colors.blue300,

  accent: "#7C3AED",
  accentSoft: "#EDE9FE",

  textPrimary: colors.gray900,
  textSecondary: colors.gray600,
  textMuted: colors.gray400,

  border: "rgba(0,0,0,0.08)",
  borderStrong: "rgba(0,0,0,0.12)",

  success: colors.blue600, // positive = blue, not green
  successLight: colors.blue100,
  danger: colors.danger600,
  dangerLight: colors.dangerLight,
  warning: colors.warning600,
  warningLight: colors.warningLight,

  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

// ── Layout tokens (vertical-first, 1080×1920) ──────────────
export const layout = {
  vertical: {
    px: 80,
    py: 72,
    safeTop: 80,
    safeBottom: 160,
    cardRadius: 20,
    cardPadding: 32,
    gap: 24,
  },
  square: {
    px: 84,
    py: 72,
    safeTop: 60,
    safeBottom: 80,
    cardRadius: 18,
    cardPadding: 28,
    gap: 20,
  },
  horizontal: {
    px: 98,
    py: 80,
    safeTop: 52,
    safeBottom: 64,
    cardRadius: 16,
    cardPadding: 28,
    gap: 22,
  },
};

// ── Typography scale (vertical-first) ───────────────────────
export const type = {
  vertical: {
    hookHeadline: { fontSize: 64, fontWeight: "800", lineHeight: 1.12, letterSpacing: "-1px" },
    sectionLabel: { fontSize: 24, fontWeight: "700", lineHeight: 1.3, letterSpacing: "1.5px", textTransform: "uppercase" },
    sectionTitle: { fontSize: 30, fontWeight: "700", lineHeight: 1.25 },
    body: { fontSize: 38, fontWeight: "500", lineHeight: 1.48 },
    bodyLarge: { fontSize: 42, fontWeight: "500", lineHeight: 1.45 },
    bigNumber: { fontSize: 72, fontWeight: "800", lineHeight: 1.05, letterSpacing: "-2px" },
    caption: { fontSize: 24, fontWeight: "600", lineHeight: 1.35 },
    label: { fontSize: 20, fontWeight: "700", lineHeight: 1.3 },
    meta: { fontSize: 18, fontWeight: "600", lineHeight: 1.4 },
    cta: { fontSize: 44, fontWeight: "800", lineHeight: 1.18, letterSpacing: "-0.5px" },
    ctaSub: { fontSize: 32, fontWeight: "600", lineHeight: 1.25 },
    disclaimer: { fontSize: 26, fontWeight: "600", lineHeight: 1.45 },
  },
  square: {
    hookHeadline: { fontSize: 56, fontWeight: "800", lineHeight: 1.12, letterSpacing: "-1px" },
    sectionLabel: { fontSize: 20, fontWeight: "700", lineHeight: 1.3, letterSpacing: "1.5px", textTransform: "uppercase" },
    sectionTitle: { fontSize: 26, fontWeight: "700", lineHeight: 1.25 },
    body: { fontSize: 34, fontWeight: "500", lineHeight: 1.48 },
    bodyLarge: { fontSize: 38, fontWeight: "500", lineHeight: 1.45 },
    bigNumber: { fontSize: 60, fontWeight: "800", lineHeight: 1.05, letterSpacing: "-2px" },
    caption: { fontSize: 20, fontWeight: "600", lineHeight: 1.35 },
    label: { fontSize: 17, fontWeight: "700", lineHeight: 1.3 },
    meta: { fontSize: 16, fontWeight: "600", lineHeight: 1.4 },
    cta: { fontSize: 38, fontWeight: "800", lineHeight: 1.18, letterSpacing: "-0.5px" },
    ctaSub: { fontSize: 28, fontWeight: "600", lineHeight: 1.25 },
    disclaimer: { fontSize: 22, fontWeight: "600", lineHeight: 1.45 },
  },
  horizontal: {
    hookHeadline: { fontSize: 62, fontWeight: "800", lineHeight: 1.12, letterSpacing: "-1px" },
    sectionLabel: { fontSize: 24, fontWeight: "700", lineHeight: 1.3, letterSpacing: "1.5px", textTransform: "uppercase" },
    sectionTitle: { fontSize: 28, fontWeight: "700", lineHeight: 1.25 },
    body: { fontSize: 40, fontWeight: "500", lineHeight: 1.48 },
    bodyLarge: { fontSize: 44, fontWeight: "500", lineHeight: 1.45 },
    bigNumber: { fontSize: 68, fontWeight: "800", lineHeight: 1.05, letterSpacing: "-2px" },
    caption: { fontSize: 22, fontWeight: "600", lineHeight: 1.35 },
    label: { fontSize: 20, fontWeight: "700", lineHeight: 1.3 },
    meta: { fontSize: 18, fontWeight: "600", lineHeight: 1.4 },
    cta: { fontSize: 48, fontWeight: "800", lineHeight: 1.18, letterSpacing: "-0.5px" },
    ctaSub: { fontSize: 34, fontWeight: "600", lineHeight: 1.25 },
    disclaimer: { fontSize: 28, fontWeight: "600", lineHeight: 1.45 },
  },
};

// ── Strategy colors ─────────────────────────────────────────
export const strategyColors = {
  "iron-condor": "#7C3AED",
  "iron-butterfly": "#9333EA",
  "bull-call-spread": "#2563EB",
  "bear-put-spread": colors.danger600,
  "covered-call": colors.blue600,
  "protective-put": colors.warning600,
  "cash-secured-put": "#0891B2",
  straddle: "#DB2777",
  strangle: "#EA580C",
  "long-call": "#2563EB",
  "long-put": colors.danger600,
  "short-call": "#EA580C",
  "short-put": "#0891B2",
  default: colors.blue600,
};

export function getStrategyColor(strategyKey = "") {
  return strategyColors[strategyKey.toLowerCase()] || strategyColors.default;
}

export function formatStrategyName(strategyKey = "") {
  return strategyKey
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// ── Aspect helpers ──────────────────────────────────────────
export function getAspect(width, height) {
  if (height > width) return "vertical";
  if (width === height) return "square";
  return "horizontal";
}

export function getLayout(width, height) {
  return layout[getAspect(width, height)];
}

export function getType(width, height) {
  return type[getAspect(width, height)];
}

/** Storyboard / screen guide tokens (1080×1920 reference — scale with getVideoScale). */
export const videoDesign = {
  neutral900: "#0F172A",
  neutral800: "#1E293B",
  neutral700: "#334155",
  neutral500: "#64748B",
  neutral400: "#94A3B8",
  neutral300: "#CBD5E1",
  neutral200: "#E2E8F0",
  neutral100: "#F1F5F9",
  blue600: "#1D4ED8",
  blue300: "#93C5FD",
  danger600: "#DC2626",
};

/**
 * Scale UI to the active composition size.
 * - Portrait (9:16): relative to 1080×1920 (storyboard reference).
 * - Landscape (16:9): use min(w/1080, h/1080) so 1920×1080 → scale 1 (not min(..., h/1920) which ~0.56 and tiny UI).
 * - Square: same as landscape for balanced density.
 */
export function getVideoScale(width, height) {
  const w = Number(width) || 1080;
  const h = Number(height) || 1920;
  if (h > w) {
    return Math.min(w / 1080, h / 1920);
  }
  return Math.min(w / 1080, h / 1080);
}
